import { Grid, Stack, TextField, RadioGroup, FormControlLabel, Radio } from '@mui/material'
import { useEffect, useState, useCallback } from 'react'
import { renderToString } from 'react-dom/server'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'
import Button from '../../../../shared/UI/Button'
import Paragraph from '../../../../shared/UI/Paragraph'
import { getLocalDateString } from '../../../../utils/date-utils'
import { manualRequestsService } from '../../../../services/myActionsService'
import { RootStore, useAppDispatch } from '../../../../redux/store'
import { showErrorMessage, showSuccessMessage } from '../../../../redux/reducers/snackbarReducer'
import { ScaMaintenanceRequest } from '../../../../types/absence-sca'
import UserErrorMessage from '../../../../utils/errorFilter'
import { AbsenceOccurrence } from '../../../../types/absence'
import { BaseResponse } from '../../../../types/base-response'
import NoDataFound from '../../../../shared/UI/NoDataFound'
import LoadingIndicator from '../../../../shared/UI/LoadingIndicator'
import { EmployeeDetailsResponse } from '../../../../types/employee'
import { sicknessTypeSortedData } from '../../../../utils/app-utils'
import { SelectOption } from '../../../../services/dashboardService'
import { AbsenceStatus } from '../../../../utils/constants'
import { DateFormats } from '../../../../api/absence/tempTypes/generalprops'
import SCANetherlandsPrint from "../../SCANetherlandsPrint"

import { handleApiError } from '../../../../utils/apiErrorHandler'
import { openPrintWindow } from '../../../../utils/printHelper'
import { useScaOperations } from '../../../../hooks/useScaOperations'
import {
  validateScaFields,
  hasValidationErrors,
  setValidationErrors
} from '../../../../utils/validationUtils'

type Props = {
  absenceOccurrence: AbsenceOccurrence
  onSubmit: () => void
  onCancel?: () => void
  setIsScaComplete?: (res: boolean) => void
}

interface scaResponseType {
  nonWorkingDays?: number
  normalWeekWorkingDays?: number
  absenceReasonText?: string
  medicalPractitionerConsultedType: string
  medicalPractitionerComments?: string
  displayName: string
  date: string
  ip: string
}

interface editableStateType {
  nonWorkingDays?: string | number
  normalWeekWorkingDays?: string | number
  absenceReasonText?: string
  absenceTypeId?: number
  medicalPractitionerConsultedType: string
  medicalPractitionerComments?: string
}

const editableStateDefaults = {
  nonWorkingDays: '',
  normalWeekWorkingDays: '',
  absenceReasonText: '',
  medicalPractitionerConsultedType: 'Na',
  medicalPractitionerComments: '',
}
const REGEX = /^[0-9\b]+$/

function NetherlandsSCA({ absenceOccurrence, onSubmit, onCancel, setIsScaComplete }: Props) {
  const [scaSubmission, setScaSubmission] = useState<ScaMaintenanceRequest>()
  const [nonWorkingDaysError, setNonWorkingDaysError] = useState<boolean>(false)
  const [normalWeekWorkingDaysError, setNormalWeekWorkingDaysError] = useState<boolean>(false)
  const [absenceReasonTextError, setAbsenceReasonTextError] = useState<boolean>(false)
  const [medicalPractitionerCommentsError, setMedicalPractitionerCommentsError] =
      useState<boolean>(false)
  const [hasChanges, setHasChanges] = useState<boolean>(false)
  const [scaResponse, setScaResponse] = useState<scaResponseType>()
  const [initialEditableState, setInitialEditableState] = useState<editableStateType>()
  const [changeableFields, setChangeableFields] = useState<editableStateType>(editableStateDefaults)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false)
  const [sicknessTypes, setSicknessTypes] = useState<SelectOption[]>([])

  const employeeDetails = useSelector<RootStore, EmployeeDetailsResponse>(
      (state: RootStore) => state.appSettings.employeeDetails
  )

  const dispatch = useAppDispatch()

  const totalHoursLost = () => {
    let result = 0
    absenceOccurrence.absenceDays.forEach(day => {
      result += day.hoursLost
    })
    return result
  }

  const awaitingSCA =
      absenceOccurrence.scaRequired &&
      (absenceOccurrence.absenceStatus.name === AbsenceStatus.AWAITING_ED ||
          absenceOccurrence.absenceStatus.name === AbsenceStatus.AWAITING_SCA)

  const scaButtonLabel = awaitingSCA ? 'Save and Sign' : 'Save and Re-submit'

  const consultedMedicalProfessional = (type: string) => {
    switch (type) {
      case 'MedicalPractitionerConsulted':
        return 'Yes'
      case 'MedicalPractitionerNotConsulted':
        return 'No'
      default:
        return 'Na'
    }
  }
  
  
  const fetchSicknessTypes = async (employeeId: number) => {
    try {
      const result = await sicknessTypeSortedData(employeeId)
      setSicknessTypes(result)
      return result
    } catch (err: any) {
      dispatch(showErrorMessage(UserErrorMessage(err)))
    }
  }

  const fetchScaData = async (absenceId: number) => {
    setIsLoading(true)
    try {
      const result = await fetchScaFromService(absenceId)

      const updatedFields = {
        nonWorkingDays: Number(result.nonWorkingDays),
        normalWeekWorkingDays: result.normalWeekWorkingDays,
        absenceReasonText: result.absenceReasonText,
        medicalPractitionerConsultedType: consultedMedicalProfessional(
            result.medicalPractitionerConsultType
        ),
        medicalPractitionerComments: result.medicalPractitionerComments,
      }

      setChangeableFields(updatedFields)
      setInitialEditableState(updatedFields)

      setScaResponse({
        nonWorkingDays: result.nonWorkingDays,
        normalWeekWorkingDays: result.normalWeekWorkingDays,
        absenceReasonText: result.absenceReasonText,
        medicalPractitionerConsultedType: consultedMedicalProfessional(
            result.medicalPractitionerConsultType
        ),
        medicalPractitionerComments: result.medicalPractitionerComments,
        displayName: result.signedByEmployeeName,
        date: new Date(result.signedDateTime).toLocaleString(navigator.language),
        ip: result.signedOnMachine,
      })

      setIsLoading(false)
      return result
    } catch (err: any) {
      setIsLoading(false)
      dispatch(showErrorMessage(UserErrorMessage(err)))
    }
  }

  const { createSca, resubmitSca, fetchScaData: fetchScaFromService } = useScaOperations()

  useEffect(() => {
    if (!awaitingSCA) {
      fetchScaData(absenceOccurrence.id)
    }

    fetchSicknessTypes(employeeDetails.employeeId)
  }, [])

  const scaDetailHasChanged = useCallback((): boolean => {
    if (changeableFields && initialEditableState) {
      return Object.keys(changeableFields).some(
          key =>
              changeableFields[key as keyof editableStateType] !==
              initialEditableState[key as keyof editableStateType]
      )
    }

    return false
  }, [initialEditableState, changeableFields])

  useEffect(() => {
    setHasChanges(scaDetailHasChanged())
  }, [scaDetailHasChanged])

  const isManager = absenceOccurrence.employeeResponse.id !== employeeDetails.employeeId
  const isDisabledReSubmitButton = (!awaitingSCA && !hasChanges) || isManager

  const createSubmissionObject = (): ScaMaintenanceRequest => ({
    ...scaSubmission,
    absenceId: absenceOccurrence.id,
    absenceTypeId: Number(absenceOccurrence.absenceStatus.id),
    employee: {
      employeeId: absenceOccurrence.employeeResponse.id,
      displayName: absenceOccurrence.employeeResponse.displayName,
    },
    department: {
      departmentId: absenceOccurrence.departmentId,
      departmentName: String(absenceOccurrence.departmentName),
    },
    dateFrom: absenceOccurrence.startDate,
    dateTo: absenceOccurrence.endDate,
    totalDaysOfAbsence: absenceOccurrence.absenceDays.length,
    daysAbsent: absenceOccurrence.absenceDays.length,
    nonWorkingDays: Number(changeableFields.nonWorkingDays) || 0,
    normalWeekWorkingDays: Number(changeableFields.normalWeekWorkingDays) || 0,
    hoursOfAbsence: totalHoursLost(),
    absenceType: {
      absenceTypeId: Number(absenceOccurrence.absenceDays[0].absenceType.absenceTypeId),
      reason: String(absenceOccurrence.reason),
      reasonType: String(absenceOccurrence.absenceDays[0].absenceType.reasonType),
    },
    absenceReasonText: changeableFields.absenceReasonText || '',
    signedDateTime: format(new Date(), DateFormats.DATE_AND_TIME),
  })

  const handleSubmit = () => {
    if (!changeableFields) {
      return
    }

    const validationErrors = validateScaFields(changeableFields)
    
    if (hasValidationErrors(validationErrors)) {
      setValidationErrors(validationErrors, {
        setNonWorkingDaysError,
        setNormalWeekWorkingDaysError,
        setAbsenceReasonTextError,
        setMedicalPractitionerCommentsError,
      })
      return
    }

    const submission = createSubmissionObject()
    setScaSubmission(submission)
    setIsScaComplete?.(true)
  }

  useEffect(() => {
    if (!scaSubmission) {
      return
    }

    const submitSca = async () => {
      try {
        setIsSubmitLoading(true)
        if (awaitingSCA) {
          await createSca(scaSubmission)
          dispatch(showSuccessMessage('SCA submitted successfully'))
        } else {
          await resubmitSca(scaSubmission)
          dispatch(showSuccessMessage('SCA resubmitted successfully'))
        }
        setIsSubmitLoading(false)
        onSubmit()
      } catch (err: any) {
        setIsSubmitLoading(false)
        dispatch(showErrorMessage(UserErrorMessage(err)))
      }
    }

    submitSca()
  }, [scaSubmission])

  const handlePrint = () => {
    const content = renderToString(
      <SCANetherlandsPrint absenceOccurrence={absenceOccurrence!} sca={scaResponse!} />
    )
    const title = `Self-Certification of Absence ${absenceOccurrence.employeeResponse.displayName}`
    openPrintWindow(content, title)
  }

  return (
    <Grid container xl={12} rowSpacing={4} columnSpacing={4} pb={4}>
      {absenceOccurrence ? (
        <>
          {/* Left Column */}
          <Grid item xs={12} lg={6} display="flex" pt={1}>
            <Grid container xl={12} alignContent="flex-start">
              {/* Name */}
              <Grid item xs={12} mb={1}>
                <Paragraph weight="bold">Name</Paragraph>
              </Grid>
              <Grid item xs={12}>
                <Paragraph>
                  {absenceOccurrence && absenceOccurrence.employeeResponse.displayName}
                </Paragraph>
              </Grid>
              {/* Period Of Absence */}
              <Grid item xs={12} display="flex" mt={4} mb={1}>
                <Paragraph weight="bold">
                  Period Of Absence (Including non-working days from the first day of absence)
                </Paragraph>
              </Grid>
              <Grid item xs={12}>
                {absenceOccurrence && (
                  <Paragraph>
                    {getLocalDateString(absenceOccurrence && absenceOccurrence.startDate)}
                    {' - '}
                    {getLocalDateString(absenceOccurrence && absenceOccurrence.endDate)}
                  </Paragraph>
                )}
              </Grid>
              {/* Total Hours Lost */}
              <Grid item xs={12} mt={4} mb={1}>
                <Paragraph weight="bold">Total number of hours absent</Paragraph>
              </Grid>
              <Grid item xs={12}>
                <Paragraph>{absenceOccurrence && totalHoursLost()}</Paragraph>
              </Grid>
              {/* Absence Reason */}
              <Grid item xs={12} mt={4} mb={1}>
                <Paragraph weight="bold">Absence Reason</Paragraph>
              </Grid>
              <Grid item xs={12}>
                <Paragraph>{absenceOccurrence && absenceOccurrence.reason}</Paragraph>
              </Grid>
              {/* Absence Classification */}
              <Grid item xs={12} display="flex" mt={4} mb={1}>
                <Paragraph weight="bold">Absence Classification</Paragraph>
              </Grid>
              <Grid item xs={12}>
                <Paragraph>
                  {absenceOccurrence && absenceOccurrence.absenceDays[0].absenceType.reasonType}
                </Paragraph>
              </Grid>
            </Grid>
          </Grid>
          {/* Right Column */}
          <Grid item xs={12} lg={6} pt={1}>
            <Grid container xl={12} alignContent="flex-start">
              {/* Total Number of days absent */}
              <Grid item xs={12} mb={1}>
                <Paragraph weight="bold">Total Number of days absent</Paragraph>
              </Grid>
              <Grid item xs={12}>
                <Paragraph>{absenceOccurrence && absenceOccurrence.absenceDays.length}</Paragraph>
              </Grid>
              {/* Total Number of days absent */}
              <Grid item xs={12} mt={4} mb={1}>
                <Paragraph weight="bold">Of which resulted in absence from work</Paragraph>
              </Grid>
              <Grid item xs={12}>
                <Paragraph>{absenceOccurrence && absenceOccurrence.absenceDays.length}</Paragraph>
              </Grid>
              {/* of which were during non-working days off */}
              <Grid item xs={12} mt={5}>
                <TextField
                  id="non-working-days"
                  label="of which were during non-working days off"
                  variant="outlined"
                  value={changeableFields?.nonWorkingDays}
                  fullWidth
                  onChange={e => {
                    setNonWorkingDaysError(false)
                    setChangeableFields({
                      ...changeableFields,
                      nonWorkingDays:
                        e.target.value !== null && REGEX.test(e.target.value) ? e.target.value : '',
                    })
                    setHasChanges(true)
                  }}
                  placeholder="0"
                  disabled={isManager}
                  error={nonWorkingDaysError}
                  InputLabelProps={{ shrink: true }}
                  onWheel={event => event.target instanceof HTMLElement && event.target.blur()}
                />
              </Grid>
              {/* Usual number of days worked per week */}
              <Grid item xs={12} mt={5}>
                <TextField
                  id="working-days"
                  label="Usual number of days worked per week must be a value between 1 - 7"
                  variant="outlined"
                  value={changeableFields?.normalWeekWorkingDays}
                  fullWidth
                  onChange={e => {
                    setNormalWeekWorkingDaysError(false)
                    setChangeableFields({
                      ...changeableFields,
                      normalWeekWorkingDays:
                        e.target.value !== null && REGEX.test(e.target.value) ? e.target.value : '',
                    })
                    setHasChanges(true)
                  }}
                  helperText="Required"
                  placeholder="0"
                  disabled={isManager}
                  error={normalWeekWorkingDaysError}
                  InputLabelProps={{ shrink: true }}
                  onWheel={event => event.target instanceof HTMLElement && event.target.blur()}
                />
              </Grid>
            </Grid>
          </Grid>
          {/* Middle Column */}
          <Grid item xs={12}>
            {/* Reason for absence */}
            <Grid item xs={12}>
              <Paragraph weight="bold" padding="0 0 14px 0">
                Reason for absence
              </Paragraph>
              <TextField
                id="reason-for-absence"
                label="Please note only general information needs to be provided"
                variant="outlined"
                value={changeableFields?.absenceReasonText}
                rows={3}
                multiline
                fullWidth
                disabled={isManager}
                onChange={e => {
                  setAbsenceReasonTextError(false)
                  setChangeableFields({
                    ...changeableFields,
                    absenceReasonText: e.target.value,
                    absenceTypeId: sicknessTypes.find(
                      x => x.displayValue === e.target.value.toString()
                    )?.value,
                  })
                  setHasChanges(true)
                }}
                error={absenceReasonTextError}
                InputLabelProps={{ shrink: true }}
                helperText="Required"
              />
            </Grid>
            <Grid item xs={12} mt="33px">
              <Paragraph weight="medium">
                I certify that I have been incapable of work because of the reasons stated above, on
                the dates shown and that this information is true and accurate. I acknowledge that
                false information will result in disciplinary action.I hereby give my employer
                permission to verify the above information.
              </Paragraph>
            </Grid>
          </Grid>
          <Grid item sm={12}>
            <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
              {scaButtonLabel !== 'Save and Sign' && (
                <>
                  {scaResponse && (
                    <Paragraph weight="medium">
                      {`Digitally signed by ${scaResponse.displayName} on ${scaResponse.date} on machine ${scaResponse.ip}`}
                    </Paragraph>
                  )}
                  <Button
                    label="Print"
                    color="secondary"
                    variant="outlined"
                    onClick={handlePrint}
                  />
                </>
              )}
              <Button label="Cancel" color="secondary" variant="outlined" onClick={onCancel} />
              {!isManager && (
                <Button
                  label={scaButtonLabel}
                  color="primary"
                  variant="outlined"
                  onClick={handleSubmit}
                  disabled={isDisabledReSubmitButton}
                  loading={isSubmitLoading}
                />
              )}
            </Stack>
          </Grid>
        </>
      ) : (
        <>
          <NoDataFound show={!isLoading && !absenceOccurrence} />
          <LoadingIndicator show={isLoading} />
        </>
      )}
    </Grid>
  )
}

export default NetherlandsSCA
